import { useColorScheme } from 'nativewind'
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react'
import { storage } from '@/lib/storage'
import { darkColors, lightColors, type ThemeColors } from '@/theme'

export type ThemeMode = 'light' | 'dark' | 'system'

const THEME_STORAGE_KEY = 'theme_mode'

interface ThemeState {
  themeMode: ThemeMode
  isDark: boolean
  colors: ThemeColors
  setThemeMode: (mode: ThemeMode) => Promise<void>
}

const ThemeContext = createContext<ThemeState | null>(null)

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [themeMode, setThemeModeState] = useState<ThemeMode>('light')
  const { colorScheme: systemScheme, setColorScheme } = useColorScheme()

  useEffect(() => {
    ;(async () => {
      const stored = await storage.get(THEME_STORAGE_KEY)
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        setThemeModeState(stored)
        setColorScheme(stored === 'system' ? 'system' : stored)
      } else {
        setColorScheme('light')
      }
    })()
  }, [setColorScheme])

  const setThemeMode = useCallback(
    async (mode: ThemeMode) => {
      await storage.set(THEME_STORAGE_KEY, mode)
      setThemeModeState(mode)
      setColorScheme(mode === 'system' ? 'system' : mode)
    },
    [setColorScheme]
  )

  const isDark = themeMode === 'dark' || (themeMode === 'system' && systemScheme === 'dark')

  const colors = isDark ? darkColors : lightColors

  return (
    <ThemeContext.Provider value={{ themeMode, isDark, colors, setThemeMode }}>
      {children}
    </ThemeContext.Provider>
  )
}

export function useTheme() {
  const ctx = useContext(ThemeContext)
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider')
  return ctx
}

export function useThemeColors(): ThemeColors {
  return useTheme().colors
}
